import styled from "@emotion/styled";
import { NATIVE_SOL } from "@stepfinance/common";
import type { NFT } from "@stepfinance/nft";
import { Breakpoints } from "@stepfinance/react";
import { Pagination } from "antd";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo } from "react";

import { useMarkets } from "../../contexts/market";
import { useNFTContext } from "../../contexts/nft";
import { fetchAndLoadNFTOffChainData } from "../../utils/nft/load";
import { slicePages } from "../../utils/utils";
import { NFTCard } from "./NFTCard";
import type { GalleryPaginationConfig } from "./NFTGallery";

interface NFTCardGridProps {
  dataSource: NFT[];
  focusedNFTRef: React.RefObject<HTMLImageElement>;
  pagination: GalleryPaginationConfig;
  onPaginationChange: (page: number, pageSize: number) => void;
  showBalances: boolean;
  setShowBalances: Dispatch<SetStateAction<boolean>>;
  onImageClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, nft: NFT) => void;
  viewingNFT?: NFT;
}

export const FilterableNFTCardGrid: React.FC<NFTCardGridProps> = ({
  dataSource,
  focusedNFTRef,
  pagination,
  onPaginationChange,
  showBalances,
  setShowBalances,
  onImageClick,
  viewingNFT,
}) => {
  const { setUserNFTs } = useNFTContext();
  const { priceInUSD } = useMarkets();

  const solPriceUSD = useMemo(() => priceInUSD(NATIVE_SOL.toBase58()), [priceInUSD]);

  // fetch offchain data for displayed NFTs on mount.
  useEffect(() => {
    const { pageSize, page } = pagination;
    const nftsToFetch = slicePages(dataSource, pageSize, page, 2);
    fetchAndLoadNFTOffChainData(nftsToFetch, setUserNFTs);

    const nftsOnCurrentPage = nftsToFetch.slice(0, pageSize);
    setShowBalances(nftsOnCurrentPage.some((nft) => nft.amount > 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nftPage = useMemo(() => {
    return slicePages(dataSource, pagination.pageSize, pagination.page);
  }, [dataSource, pagination.page, pagination.pageSize]);

  return (
    <>
      <NFTCardsContainer>
        {nftPage.map((nft: NFT) => (
          <NFTCard
            nft={nft}
            key={nft.address.toBase58()}
            onImageClick={onImageClick}
            solPriceUsd={solPriceUSD || 0}
            refToUse={focusedNFTRef}
            canUseRef={nft.address.toBase58() === viewingNFT?.address.toBase58()}
            showBalances={showBalances}
          />
        ))}
      </NFTCardsContainer>
      <PaginationContainer>
        <Pagination
          onChange={onPaginationChange}
          hideOnSinglePage={pagination.hideOnSinglePage}
          showSizeChanger
          pageSizeOptions={pagination.pageSizeOptions}
          pageSize={pagination.pageSize}
          total={dataSource.length}
          current={pagination.page}
        />
      </PaginationContainer>
    </>
  );
};

const NFTCardsContainer = styled.div`
  padding-top: 20px;
  display: grid;
  gap: 18px;

  --mobile-size: 1fr;
  --size-2x2: 1fr 1fr;
  --size-3x3: 1fr 1fr 1fr;
  --size-4x4: 1fr 1fr 1fr 1fr;

  grid-template-columns: var(--mobile-size);
  @media (min-width: ${Breakpoints.S}px) {
    grid-template-columns: var(--size-2x2);
  }
  @media (min-width: ${Breakpoints.M}px) {
    grid-template-columns: var(--size-3x3);
  }
  @media (min-width: ${Breakpoints.L}px) {
    grid-template-columns: var(--size-3x3);
  }
  @media (min-width: ${Breakpoints.XXL}px) {
    grid-template-columns: var(--size-4x4);
  }

  .ant-card {
    .ant-card-cover,
    .thumbnail img,
    .thumbnail video {
      width: 100%;
      height: auto;

      cursor: pointer;
      object-fit: cover;
      aspect-ratio: 1;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 10px 0;

  > ul.ant-pagination {
    margin-left: auto;
  }
`;
